import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatISTDateTime } from "../../lib/admin-time";
const labels = {
  not_sent: "Not sent",
  claimed: "Outcome unknown",
  sent: "Sent",
  failed: "Failed",
  suppressed: "Suppressed",
};
export default function CheckoutReminderAccounts({
  api,
  filters,
  refresh,
  onEmail,
}) {
  const [outcome, setOutcome] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { startDate, endDate, q } = filters;
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, q, outcome]);
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    api("/checkout-reminders", { startDate, endDate, q, outcome, page })
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active)
          setError(
            err.response?.data?.error ||
              "Could not load checkout accounts. Please try again."
          );
      });
    return () => {
      active = false;
    };
  }, [api, startDate, endDate, q, outcome, page, refresh]);
  return (
    <section className="email-panel email-log" aria-label="Checkout accounts">
      <div className="email-section-title">
        <div>
          <h2>Checkout accounts</h2>
          <p className="email-muted">
            One row per account, by latest checkout creation date. Email status
            filters above apply to the recipient log.
          </p>
        </div>
        <label className="checkout-outcome-filter">
          Reminder outcome
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="">All outcomes</option>
            {Object.entries(labels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? (
        <p className="email-error" role="alert">
          {error}
        </p>
      ) : !data ? (
        <p className="email-loading" role="status">
          Loading checkout accounts…
        </p>
      ) : (
        <>
          <p className="email-muted">
            {data.summary.accounts} accounts · {data.summary.incomplete}{" "}
            incomplete · {data.summary.upgraded} completed or upgraded ·{" "}
            {data.summary.opted_out} opted out · {data.summary.failed} send
            failures · {data.summary.claimed} unresolved send outcomes
          </p>
          <div className="email-table-scroll">
            <table>
              <thead>
                <tr>
                  {[
                    "Account",
                    "Latest checkout",
                    "Current plan",
                    "Reminder",
                    "Engagement",
                    "Account outcome",
                  ].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((a) => (
                  <tr key={a.user_id}>
                    <td>
                      <Link
                        className="email-recipient"
                        to={`/users/${encodeURIComponent(a.user_id)}`}
                      >
                        {a.recipient || a.user_id}
                      </Link>
                      {a.recipient && <small>{a.user_id}</small>}
                      {!a.recipient && (
                        <small>No recorded email recipient</small>
                      )}
                    </td>
                    <td>
                      {a.plan}
                      <small>
                        {a.status} · {formatISTDateTime(a.created_at)}
                      </small>
                      <small>{a.checkout_id}</small>
                    </td>
                    <td>
                      {a.current_plan || "Free"}
                      {a.subscription_status && (
                        <small>{a.subscription_status}</small>
                      )}
                    </td>
                    <td>
                      <span
                        className={`email-status email-status-${a.reminder_outcome}`}
                      >
                        {labels[a.reminder_outcome] || a.reminder_outcome}
                      </span>
                      <small>
                        {formatISTDateTime(
                          a.accepted_at ||
                            a.reminder_sent_at ||
                            a.reminder_claimed_at
                        )}
                      </small>
                      {a.email_id && (
                        <button
                          className="email-recipient"
                          onClick={() => onEmail(a.email_id)}
                        >
                          View email
                        </button>
                      )}
                      {a.last_check_reason && (
                        <small>{a.last_check_reason.replace(/_/g, " ")}</small>
                      )}
                    </td>
                    <td>
                      {a.tracking_enabled
                        ? `${a.open_count || 0} opens · ${
                            a.click_count || 0
                          } clicks`
                        : "Tracking unavailable"}
                      {a.delivered_at && (
                        <small>
                          Delivered {formatISTDateTime(a.delivered_at)}
                        </small>
                      )}
                      {a.first_open_at && (
                        <small>
                          First open {formatISTDateTime(a.first_open_at)}
                        </small>
                      )}
                    </td>
                    <td>
                      {a.upgraded_at ? (
                        <>
                          Upgraded
                          <small>{formatISTDateTime(a.upgraded_at)}</small>
                        </>
                      ) : (
                        "No recorded upgrade"
                      )}
                      {a.opted_out_at && (
                        <small>
                          Opted out {formatISTDateTime(a.opted_out_at)}
                        </small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!data.accounts.length && (
            <p className="email-empty">
              No checkout accounts match these filters.
            </p>
          )}
          <footer className="email-pagination">
            <span>
              Page {page} of{" "}
              {Math.max(1, Math.ceil(data.total / data.pageSize))}
            </span>
            <div>
              <button
                className="email-button"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous accounts
              </button>
              <button
                className="email-button"
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next accounts
              </button>
            </div>
          </footer>
        </>
      )}
    </section>
  );
}
